import { from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export enum DeviceType {
  desktop = 'Desktop',
  mobile = 'Mobile',
  tablet = 'Tablet',
  unknown = 'Unknown',
}
export enum PlatformType {
  windows = 'Windows',
  macos = 'macOS',
  ios = 'iOS',
  android = 'Android',
  linux = 'Linux',
  chromeos = 'chromeOS',
  unknown = 'Unknown',
}
export interface DeviceInfo {
  deviceType: DeviceType;
  platform: PlatformType;
}

// -----------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------

function normalizePlatform(platform: string): PlatformType {
  const p = platform.toLowerCase();
  if (p.includes('win')) return PlatformType.windows;
  if (p.includes('mac')) return PlatformType.macos;
  if (p.includes('android')) return PlatformType.android;
  if (p.includes('ios')) return PlatformType.ios;
  if (p.includes('chrome')) return PlatformType.chromeos;
  if (p.includes('linux')) return PlatformType.linux;
  return PlatformType.unknown;
}

function resolveDeviceType(mobile: boolean, formFactor: string[] | undefined): DeviceType {
  if (!mobile) return DeviceType.desktop;
  if (formFactor?.some((f) => /tablet/i.test(f))) return DeviceType.tablet;
  return DeviceType.mobile;
}

// -----------------------------------------------------------------
// UA string fallback
// -----------------------------------------------------------------

function detectDeviceFromUA(ua: string): DeviceInfo {
  const tabletRegex =
    /ipad|android(?!.*mobile)|tablet|kindle|silk|playbook|nexus\s?(7|10)|sm-t\d+/i;
  const mobileRegex =
    /iphone|ipod|android.*mobile|windows phone|blackberry|bb10|opera mini|iemobile/i;

  // iPadOS 13+ reports macOS in its UA to request desktop sites — detect via touch capability
  const isIPadOS = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;

  let deviceType: DeviceType;
  if (isIPadOS) deviceType = DeviceType.tablet;
  else if (tabletRegex.test(ua)) deviceType = DeviceType.tablet;
  else if (mobileRegex.test(ua)) deviceType = DeviceType.mobile;
  else deviceType = DeviceType.desktop;

  // Important: don't change the order of the checks
  let platform: PlatformType = PlatformType.unknown;
  if (isIPadOS) platform = PlatformType.ios;
  else if (/windows/i.test(ua)) platform = PlatformType.windows;
  else if (/iphone|ipad|ipod/i.test(ua)) platform = PlatformType.ios;
  else if (/android/i.test(ua)) platform = PlatformType.android;
  else if (/macintosh|mac os/i.test(ua)) platform = PlatformType.macos;
  else if (/linux/i.test(ua)) platform = PlatformType.linux;
  else if (/cros/i.test(ua)) platform = PlatformType.chromeos;

  return { deviceType, platform };
}

// -----------------------------------------------------------------
// Public API
// -----------------------------------------------------------------

export function detectDevice(): Observable<DeviceInfo> {
  if (navigator.userAgentData) {
    return from(navigator.userAgentData.getHighEntropyValues(['platform', 'formFactor'])).pipe(
      map((hints) => ({
        platform: normalizePlatform(hints.platform ?? ''),
        deviceType: resolveDeviceType(navigator.userAgentData!.mobile, hints.formFactors),
      })),
    );
  }

  return of(detectDeviceFromUA(navigator.userAgent));
}
